/**
 * UK sort code (6 digits) and account number (8 digits), format only — no Vocalink modulus check.
 * No IBAN: the bank's four letters are not derivable from the sort code.
 * @module banking/schemes/gb-sort-code
 */

import type { BankingValidationError } from '../validation';
import { BankAccountScheme, type BankAccountSchemeDefinition } from './bank-account-scheme';
import {
  bicErrors,
  compactDigits,
  normalizeBic,
  REQUIRED_ERRORS,
  territoryCountry
} from './scheme-helpers';

/** Countries served by UK sort codes; the first is the default. */
export const GB_SORT_CODE_COUNTRIES: readonly string[] = ['GB', 'JE', 'GG', 'IM'];

const SORT_CODE_REGEX = /^\d{6}$/;
const ACCOUNT_NUMBER_REGEX = /^\d{8}$/;

export const gbSortCodeScheme: BankAccountSchemeDefinition = {
  scheme: BankAccountScheme.GB_SORT_CODE,

  validate(account) {
    const errors: BankingValidationError[] = [];
    const sortCode = compactDigits(account.bankIdentifier);
    const accountNumber = compactDigits(account.accountNumber);

    if (!sortCode) errors.push(REQUIRED_ERRORS.bankIdentifier);
    else if (!SORT_CODE_REGEX.test(sortCode)) {
      errors.push({ code: 'bankIdentifierInvalid', message: 'Sort code must be 6 digits' });
    }
    if (!accountNumber) errors.push(REQUIRED_ERRORS.accountNumber);
    else if (!ACCOUNT_NUMBER_REGEX.test(accountNumber)) {
      errors.push({ code: 'accountNumberInvalid', message: 'Account number must be 8 digits' });
    }
    errors.push(...territoryCountry(account.countryCode, GB_SORT_CODE_COUNTRIES).errors);
    errors.push(...bicErrors(account.bic));

    return { valid: errors.length === 0, errors };
  },

  normalize(account) {
    const sortCode = compactDigits(account.bankIdentifier);
    const accountNumber = compactDigits(account.accountNumber);
    const { country } = territoryCountry(account.countryCode, GB_SORT_CODE_COUNTRIES);
    if (!sortCode || !SORT_CODE_REGEX.test(sortCode)) return null;
    if (!accountNumber || !ACCOUNT_NUMBER_REGEX.test(accountNumber)) return null;
    if (!country) return null;

    return {
      scheme: BankAccountScheme.GB_SORT_CODE,
      countryCode: country,
      iban: null,
      bankIdentifier: sortCode,
      accountNumber,
      bic: normalizeBic(account.bic)
    };
  },

  toIban() {
    return null;
  }
};
